import { Box, CircularProgress, Grid, ThemeProvider } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { AddButton } from '../components/AddButton'
import { CustomExplain } from '../components/CustomExplain'
import { CustomPaper } from '../components/CustomPaper'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { WordList } from '../components/WordList'
import { loadStatus } from '../constants/loadStatus'
import { textTheme } from '../constants/theme'
import { useAuth } from '../hooks/useAuth'
import { useC401WordGet } from '../hooks/useC401WordGet'
import { useC402WordAdd } from '../hooks/useC402WordAdd'
import { useC403WordDelete } from '../hooks/useC403WordDelete'
import {useC407WordUpdate } from '../hooks/useC407WordUpdate'
import type { WordInfo } from '../interfaces/WordIf'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'
import { useDeviceCapability } from '../util/DeviceCapabilityContext'

// 強調単語画面
export const Word = () => {
  const auth = useAuth()
  const c401 = useC401WordGet()
  const c402 = useC402WordAdd()
  const c403 = useC403WordDelete()
  const c407 = useC407WordUpdate()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  // 強調単語配列
  const [words, setWords] = useState<WordInfo[]>([])
  
  const { supportsOverwrite } = useDeviceCapability()

  // ローディングステータス監視
  useEffect(() => {
    if (
      c401.loading === loadStatus.LOADING ||
      c402.loading === loadStatus.LOADING ||
      c403.loading === loadStatus.LOADING ||
      c407.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c401.loading, c402.loading, c403.loading, c407.loading])

  // 強調単語追加ハンドラ
  const WordAddHandler = async (text: string) => {
    const result = await c402.execWordAdd(text,{ overwrite: supportsOverwrite })
    if (result.success) {
      const result = await c401.execWordGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setWords(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // 強調単語削除ハンドラ
  const WordDelHandler = async (text: string) => {
    const result = await c403.execWordDelete(text)
    if (result.success) {
      const result = await c401.execWordGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setWords(result.info!)
        } else {
          setWords([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 強調単語の編集
  const wordUpdateHandler = async (old_wordText: string, wordText: string) => {
    const result = await c407.execWordUpdate(auth.deviceId, old_wordText, wordText)
    if (result.success) {
      const result = await c401.execWordGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setWords(result.info!)
        } else {
          setWords([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      const result = await c401.execWordGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setWords(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <ThemeProvider theme={textTheme}>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <CustomTitle>{translation['label_emphasis_words']}</CustomTitle>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            {(c401.loading === loadStatus.LOADING ||
              c402.loading === loadStatus.LOADING ||
              c403.loading === loadStatus.LOADING ||
              c407.loading === loadStatus.LOADING) && <CircularProgress />}
          </Box>
          <Box>
            <CustomExplain>{translation['message_emphasis_word_info']}</CustomExplain>
          </Box>
          <Box
            sx={{
              marginTop: '40px',
            }}
          >
            <CustomPaper>
              <Grid container spacing={2}>
                <Grid item lg={6} xl={3.7}>
                  <AddButton title={translation['label_emphasis_words']} onAdd={WordAddHandler} words={words} overwrite={supportsOverwrite}>
                    {translation['button_add_new']}
                  </AddButton>
                </Grid>
                <WordList items={words} onDeleteWord={WordDelHandler} onUpdateWord={wordUpdateHandler}/>
              </Grid>
            </CustomPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </ThemeProvider>
  )
}
